import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UploadService } from '../../services/upload/upload.service';
import { UploadType } from '../../services/upload/upload-type';
import { environment } from '../../../environments/environment';
import { FileUploadStateService } from '../../file-upload-state.service';

declare var Dropzone: any;

export interface DropzoneConfig {
  url: string;
  method?: string;
  timeout?: number;
  parallelUploads?: number;
  uploadMultiple?: boolean;
  chunking?: boolean;
  chunkSize?: number;
  retryChunks?: boolean;
  retryChunksLimit?: number;
  maxFilesize?: number; // In MB
  paramName?: string;
  createImageThumbnails?: boolean;
  maxThumbnailFilesize?: number; // In MB
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  thumbnailMethod?: string;
  resizeWidth?: number;
  resizeHeight?: number;
  resizeMimeType?: string;
  resizeQuality?: number;
  resizeMethod?: string;
  filesizeBase?: number;
  maxFiles?: number;
  params?: { [key: string]: any };
  headers?: { [key: string]: string };
  clickable?: boolean | string;
  ignoreHiddenFiles?: boolean;
  acceptedFiles?: string;
  useDirectGcs?: boolean; // Opt into direct-to-GCS uploads (see UploadService.shouldUseDirectGcs)
  autoProcessQueue?: boolean;
  autoQueue?: boolean;
  addRemoveLinks?: boolean;
  previewsContainer?: string | HTMLElement;
  hiddenInputContainer?: HTMLElement;
  capture?: string;
  renameFilename?: (name: string) => string;
  dictDefaultMessage?: string;
  dictFallbackMessage?: string;
  dictFallbackText?: string;
  dictFileTooBig?: string;
  dictInvalidFileType?: string;
  dictResponseError?: string;
  dictCancelUpload?: string;
  dictCancelUploadConfirmation?: string;
  dictRemoveFile?: string;
  dictRemoveFileConfirmation?: string;
  dictMaxFilesExceeded?: string;
  dictFileSizeUnits?: { [key: string]: string };
  forceFallback?: boolean;
}

@Directive({
  selector: '[appDropzone]',
  standalone: true,
})
export class DropzoneDirective implements OnInit, OnDestroy {
  apiUrl: string = `${environment.apiBaseUrl}/api/v1`;
  url = `${this.apiUrl}/buckets/upload/instrument-run`;
  @Input() config: DropzoneConfig = { url: this.url };
  @Input() useChunking = false;
  @Input() chunkSize = 1024 * 1024; // 1MB
  // @Input() folderPath?: string;
  @Input({ required: true }) featureName!: UploadType;
  // @Input() resourceId?: string;
  @Input() fileName?: string | null;
  @Input() fileDescription?: string | null;
  @Input() files?: any = [];

  @Output() fileAdded = new EventEmitter<File>();
  @Output() fileRemoved = new EventEmitter<File>();
  @Output() uploadProgress = new EventEmitter<{
    file: File;
    progress: number;
  }>();
  @Output() uploadSuccess = new EventEmitter<{ file: File; response: any }>();
  @Output() uploadError = new EventEmitter<{ file: File; error: any }>();
  @Output() uploadDuplicateError = new EventEmitter<string>();
  @Output() uploadCancelled = new EventEmitter<File>();
  @Output() maxFilesExceeded = new EventEmitter<File[]>();
  @Output() fileRejected = new EventEmitter<{ file: File; reason: string }>();
  @Output() onComplete = new EventEmitter<{ file: File }>();

  private dropzone: any;
  private hasBeenDestroyed = false;
  private filesMap = new Set();

  constructor(
    private el: ElementRef,
    private uploadService: UploadService,
    private fileUploadStateService: FileUploadStateService
  ) {}

  ngOnInit(): void {
    this.createFilesMap();
    // Load Dropzone dynamically if needed
    if (typeof Dropzone === 'undefined') {
      console.error('Dropzone is not loaded. Make sure to include it in your project.');
      return;
    }

    // Prevent Dropzone from auto-discovering this element
    Dropzone.autoDiscover = false;

    // Apply chunking configuration if enabled
    if (this.useChunking) {
      this.config = {
        ...this.config,
        chunking: true,
        chunkSize: this.chunkSize,
        retryChunks: true,
        retryChunksLimit: 3,
        parallelUploads: 4,
      };
    }

    // Ensure some defaults
    const finalConfig = {
      autoProcessQueue: false, // Important: we don't want Dropzone to upload
      dictDefaultMessage: 'Drop files here or click to upload',
      dictFileTooBig: 'File is too big ({{filesize}}MB). Max size: {{maxFilesize}}MB.',
      dictInvalidFileType: 'You cannot upload files of this type.',
      dictResponseError: 'Server error: {{statusCode}}',
      dictMaxFilesExceeded: 'You cannot upload any more files.',
      createImageThumbnails: false,
      maxFiles: null,
      ...this.config,
    };

    // Create the Dropzone instance
    this.dropzone = new Dropzone(this.el.nativeElement, finalConfig);

    // Set up event handlers
    this.setupEventHandlers();
  }

  ngOnDestroy(): void {
    this.hasBeenDestroyed = true;
    if (this.dropzone) {
      this.dropzone.destroy();
    }
  }

  private setupEventHandlers(): void {
    // File added to Dropzone
    this.dropzone.on('addedfile', async (file: any) => {
      //duplicate file check
      if (this.featureName && this.featureName === UploadType.ReferenceFileManagement) {
        if (this.uploadService.getFileMap().has(file.name)) {
          this.uploadDuplicateError.emit('File(s) already exist');
          return;
        } else {
          this.uploadService.setFileMap(file.name);
        }
      }
      // Check if file type is allowed
      if (this.config.acceptedFiles && !this.uploadService.isFileTypeAllowed(file, this.config.acceptedFiles)) {
        this.dropzone.removeFile(file);
        this.fileRejected.emit({
          file: file,
          reason: this.uploadService.getFileTypeErrorMessage(file, this.config.acceptedFiles),
        });
        return;
      }

      // Check file size
      if (this.config.maxFilesize && file.size > this.config.maxFilesize * 1024 * 1024) {
        this.dropzone.removeFile(file);
        this.fileRejected.emit({
          file: file,
          reason: this.uploadService.getFileSizeErrorMessage(file, this.config.maxFilesize * 1024 * 1024),
        });
        return;
      }

      // Prepare upload options
      const uploadOptions = {
        chunkSize: this.useChunking ? this.chunkSize : 0,
        headers: this.config.headers,
        useDropzoneChunkFormat: true, // Use Dropzone format
        metadata: this.config.params,
        uploadType: this.featureName,
        fileName: this.fileName,
        fileDescription: this.fileDescription,
        useDirectGcs: this.config.useDirectGcs,
      };

      // Start upload via our service
      const uploadId = await this.uploadService.uploadFile(file, this.config.url, uploadOptions);
      this.fileAdded.emit(file);
      // Store upload ID on the file
      file.uploadId = uploadId;

      // Track progress
      this.trackUploadProgress(file, uploadId);
    });

    // File removed from Dropzone
    this.dropzone.on('removedfile', (file: any) => {
      this.fileRemoved.emit(file);

      // Free up the duplicate-name reservation so the same file can be re-added
      this.releaseFileMapEntry(file);

      if (file.uploadId && !this.hasBeenDestroyed) {
        // Cancel upload if in progress
        this.uploadService.cancelUpload(file.uploadId);
      }
    });

    // Listen for max files exceeded
    this.dropzone.on('maxfilesexceeded', (files: File[]) => {
      this.maxFilesExceeded.emit(files);
    });
  }
  private trackUploadProgress(file: any, uploadId: string): void {
    // Subscribe to the uploads observable to track this file
    const subscription = this.uploadService.uploads$.subscribe((uploads) => {
      const uploadInfo = uploads.find((u) => u.id === uploadId);

      if (uploadInfo) {
        // Update Dropzone's UI with the progress
        file.upload = file.upload || { progress: 0 };
        file.upload.progress = uploadInfo.progress;

        // Emit progress event
        this.uploadProgress.emit({ file, progress: uploadInfo.progress });

        // Trigger Dropzone progress event for UI updates
        this.dropzone.emit('uploadprogress', file, uploadInfo.progress);

        // Handle different statuses
        switch (uploadInfo.status) {
          case 'completed':
            if (uploadInfo.progress === 100) {
              this.dropzone.emit('success', file, uploadInfo.response || {});
              this.dropzone.emit('complete', file);

              //checking if all uploads are completed
              const isComplete =
                this.getDropzoneInstance().getUploadingFiles().length === 0 &&
                this.getDropzoneInstance().getQueuedFiles().length === 0;

              if (isComplete) {
                this.fileUploadStateService.notifyUploadsComplete();
              }
              this.uploadSuccess.emit({ file, response: uploadInfo.response });
            }
            break;

          case 'error':
            this.releaseFileMapEntry(file);
            this.dropzone.emit('error', file, uploadInfo.error || 'Upload failed');
            this.dropzone.emit('complete', file);
            this.uploadError.emit({ file, error: uploadInfo.error });
            break;

          case 'cancelled':
            this.releaseFileMapEntry(file);
            this.dropzone.emit('complete', file);
            this.uploadCancelled.emit(file);
            break;
        }

        // Clean up subscription if upload is finished
        if (uploadInfo.status === 'completed' || uploadInfo.status === 'error' || uploadInfo.status === 'cancelled') {
          if (subscription) {
            subscription.unsubscribe();
          }
        }
      }
    });

    // Store subscription for cleanup
    file.progressSubscription = subscription;
  }

  // Public methods

  getDropzoneInstance(): any {
    return this.dropzone;
  }

  resetDropzone(): void {
    if (this.dropzone) {
      this.dropzone.removeAllFiles(true);
    }
  }

  // Manually add files
  addFiles(files: File[]): void {
    if (this.dropzone) {
      files.forEach((file) => {
        this.dropzone.addFile(file);
      });
    }
  }

  // Cancel all uploads
  cancelAll(): void {
    if (this.dropzone) {
      // Remove all files from Dropzone
      this.dropzone.getQueuedFiles().forEach((file: any) => {
        this.dropzone.removeFile(file);
      });

      this.dropzone.getUploadingFiles().forEach((file: any) => {
        this.dropzone.removeFile(file);
      });
    }
  }

  // Free up the duplicate-name reservation for a file (reference file management only)
  private releaseFileMapEntry(file: any): void {
    if (this.featureName && this.featureName === 'reference_file_management') {
      this.uploadService.removeFileMap(file.name);
    }
  }

  private createFilesMap() {
    if (this.files) {
      this.files.forEach((file: any) => {
        if (file && file.data && file.data.fileName && !this.uploadService.getFileMap().has(file.data.fileName)) {
          this.uploadService.setFileMap(file.data.fileName);
        }
      });
    }
  }
}
